import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { IconButton, Tooltip } from "@mui/material";
import {
  type CSSProperties,
  type Ref,
  useCallback,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";

export function ClearSearchButton(props: {
  onClear: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  style?: CSSProperties;
  /** Match a custom dropdown layer; the tooltip portal follows it in DOM order. */
  tooltipZIndex?: CSSProperties["zIndex"];
}) {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={t("Clear search")}
      slotProps={{ popper: { style: { zIndex: props.tooltipZIndex } } }}
    >
      <IconButton
        type="button"
        data-search-clear="true"
        aria-label={t("Clear search")}
        disabled={props.disabled}
        size="small"
        style={props.style}
        sx={{
          width: 24,
          height: 24,
          minWidth: 24,
          padding: "3px",
          borderRadius: "4px",
          color: "inherit",
          flexShrink: 0,
          "&:hover": { backgroundColor: "rgba(128, 128, 128, 0.12)" },
          "&.Mui-focusVisible": {
            outline: "2px solid var(--app-form-active-menu)",
            outlineOffset: "1px"
          }
        }}
        onTouchStartCapture={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ")
            event.stopPropagation();
        }}
        onKeyUp={(event) => {
          if (event.key === "Enter" || event.key === " ")
            event.stopPropagation();
        }}
        onClickCapture={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onClear(event);
        }}
      >
        <HighlightOffIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

/** Opt-in search controls reuse the input's normal change flow, including
 * uncontrolled consumers. No form field is made clearable implicitly. */
export function useClearSearch(params: {
  value?: unknown;
  defaultValue?: unknown;
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>;
  onClear?: () => void;
}) {
  const input = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [typed, setTyped] = useState(String(params.defaultValue ?? ""));
  const forwardedRef = params.inputRef;
  const inputRef = useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      input.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef)
        (forwardedRef as { current: typeof node }).current = node;
    },
    [forwardedRef]
  );
  return {
    inputRef,
    text: params.value === undefined ? typed : String(params.value ?? ""),
    capture: (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setTyped(event.currentTarget.value);
    },
    clear: () => {
      const node = input.current;
      if (!node || node.disabled || node.readOnly) return;
      const prototype =
        node instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype;
      if (params.onClear) {
        node.value = "";
        params.onClear();
      } else {
        Object.getOwnPropertyDescriptor(prototype, "value")?.set?.call(
          node,
          ""
        );
        // One genuine input event follows the consumer's canonical change
        // flow, with the real target/currentTarget and without a submit.
        node.dispatchEvent(new Event("input", { bubbles: true }));
      }
      setTyped("");
      node.focus({ preventScroll: true });
    }
  };
}
